// src/pages/FamilyFeedPage.js
// Updated: 06/20/2026

import React, { useState, useEffect } from 'react';
import Layout from '../components/common/layout/Layout';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { db } from '../firebase/config';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc } from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { FaPaperPlane, FaTrash, FaUserCircle, FaComments, FaReply, FaChevronDown, FaChevronUp } from 'react-icons/fa';

// Sub-component to handle individual posts and their replies
const PostItem = ({ post, familyId, userData, isHost, onDeletePost }) => {
  const [replies, setReplies] = useState([]);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [showReplies, setShowReplies] = useState(false); // NEW STATE for collapsing/expanding
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  // Fetch replies for this specific post
  useEffect(() => {
    const repliesRef = collection(db, `families/${familyId}/posts/${post.id}/replies`);
    const q = query(repliesRef, orderBy('createdAt', 'asc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setReplies(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [familyId, post.id]);

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    
    setSubmittingReply(true);
    try {
      await addDoc(collection(db, `families/${familyId}/posts/${post.id}/replies`), {
        message: replyText.trim(),
        authorId: userData.uid,
        authorName: `${userData.firstName} ${userData.lastName}`,
        authorPhoto: userData.profilePictureUrl || null,
        createdAt: serverTimestamp()
      });
      setReplyText('');
      setShowReplyInput(false);
      setShowReplies(true); // Automatically expand to show the newly added reply
    } catch (error) {
      console.error("Error adding reply: ", error);
      toast.error("Failed to post reply.");
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (window.confirm("Are you sure you want to delete this reply?")) {
      try {
        await deleteDoc(doc(db, `families/${familyId}/posts/${post.id}/replies/${replyId}`));
        toast.success("Reply deleted.");
      } catch (error) {
        toast.error("Failed to delete reply.");
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100 group relative">
      {/* Delete Post Button (Visible to Author or Family Hosts) */}
      {(isHost || userData?.uid === post.authorId) && (
        <button 
            onClick={() => onDeletePost(post.id)}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Delete Post"
        >
            <FaTrash size={14}/>
        </button>
      )}
      
      {/* Main Post Content */}
      <div className="flex gap-4">
        <div className="flex-shrink-0 pt-1">
            {post.authorPhoto ? (
                <img src={post.authorPhoto} alt="" className="h-10 w-10 rounded-full object-cover shadow-sm"/>
            ) : (
                <FaUserCircle className="h-10 w-10 text-gray-300"/>
            )}
        </div>
        <div className="flex-grow">
            <div className="flex items-baseline gap-2 mb-1">
                <h4 className="font-bold text-gray-900">{post.authorName}</h4>
                <span className="text-xs text-gray-500">
                    {post.createdAt ? formatDistanceToNow(post.createdAt.toDate(), { addSuffix: true }) : 'Just now'}
                </span>
            </div>
            <p className="text-gray-800 whitespace-pre-wrap">{post.message}</p>
            
            {/* Post Action Bar */}
            <div className="mt-3 flex items-center gap-4">
              <button 
                onClick={() => setShowReplyInput(!showReplyInput)}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-primary transition-colors font-medium"
              >
                <FaReply size={12} /> Reply
              </button>
              
              {/* Expand/Collapse Replies Button */}
              {replies.length > 0 && (
                <button 
                  onClick={() => setShowReplies(!showReplies)}
                  className="flex items-center gap-1 text-sm text-primary font-medium hover:underline transition-colors"
                >
                  {showReplies ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                  {showReplies ? 'Hide replies' : `View ${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}`}
                </button>
              )}
            </div>
        </div>
      </div>

      {/* Replies Thread (Conditionally Rendered based on showReplies state) */}
      {showReplies && replies.length > 0 && (
        <div className="mt-4 ml-12 pl-4 border-l-2 border-gray-100 space-y-4">
          {replies.map((reply) => (
            <div key={reply.id} className="relative group/reply flex gap-3">
              <div className="flex-shrink-0 pt-1">
                  {reply.authorPhoto ? (
                      <img src={reply.authorPhoto} alt="" className="h-8 w-8 rounded-full object-cover shadow-sm"/>
                  ) : (
                      <FaUserCircle className="h-8 w-8 text-gray-300"/>
                  )}
              </div>
              <div className="flex-grow bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between items-start">
                  <div className="flex items-baseline gap-2 mb-1">
                      <h5 className="font-bold text-sm text-gray-900">{reply.authorName}</h5>
                      <span className="text-[11px] text-gray-500">
                          {reply.createdAt ? formatDistanceToNow(reply.createdAt.toDate(), { addSuffix: true }) : 'Just now'}
                      </span>
                  </div>
                  {/* Delete Reply Button */}
                  {(isHost || userData?.uid === reply.authorId) && (
                    <button 
                        onClick={() => handleDeleteReply(reply.id)}
                        className="text-gray-400 hover:text-red-500 opacity-0 group-hover/reply:opacity-100 transition-opacity"
                        title="Delete Reply"
                    >
                        <FaTrash size={12}/>
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{reply.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reply Input Box */}
      {showReplyInput && (
        <div className="mt-4 ml-12 pl-4 border-l-2 border-gray-100">
          <form onSubmit={handleReplySubmit} className="flex gap-3 items-start">
             <div className="flex-shrink-0 pt-1">
                {userData?.profilePictureUrl ? (
                    <img src={userData.profilePictureUrl} alt="" className="h-8 w-8 rounded-full object-cover shadow-sm"/>
                ) : (
                    <FaUserCircle className="h-8 w-8 text-gray-300"/>
                )}
            </div>
            <div className="flex-grow relative">
              <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Write a reply..."
                  className="w-full border border-gray-300 rounded-lg p-2 pr-12 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none"
                  rows="2"
                  autoFocus
                  required
              ></textarea>
              <button 
                  type="submit" 
                  disabled={submittingReply || !replyText.trim()}
                  className="absolute bottom-2 right-2 p-1.5 bg-primary text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  title="Send Reply"
              >
                  <FaPaperPlane size={12}/>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// Main Feed Component
const FamilyFeedPage = () => {
  const { userData } = useAuth();
  const { familyData, isHost } = useFamily();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch parent posts
  useEffect(() => {
    if (!familyData?.id) return;
    
    const postsRef = collection(db, `families/${familyData.id}/posts`);
    const q = query(postsRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postsData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching posts:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [familyData?.id]);

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;
    
    setSubmitting(true);
    try {
      await addDoc(collection(db, `families/${familyData.id}/posts`), {
        message: newPost.trim(),
        authorId: userData.uid,
        authorName: `${userData.firstName} ${userData.lastName}`,
        authorPhoto: userData.profilePictureUrl || null,
        createdAt: serverTimestamp()
      });
      setNewPost('');
    } catch (error) {
      console.error("Error adding post: ", error);
      toast.error("Failed to post message.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm("Are you sure you want to delete this post? This will also delete all replies.")) {
      try {
        await deleteDoc(doc(db, `families/${familyData.id}/posts/${postId}`));
        toast.success("Post deleted.");
      } catch (error) {
        toast.error("Failed to delete post.");
      }
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-6">
        <div className="bg-white rounded-lg shadow-md p-6 mb-8 text-center border-b-4 border-primary">
            <FaComments className="text-4xl text-primary mx-auto mb-3"/>
            <h1 className="text-3xl font-bold text-neutral-gray">Family Message Board</h1>
            <p className="text-gray-600 mt-2">Chat, banter, and coordinate with the rest of the {familyData?.familyName} family!</p>
        </div>

        {/* Parent Post Creation Box */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <form onSubmit={handlePostSubmit}>
                <div className="flex gap-4">
                    <div className="flex-shrink-0">
                        {userData?.profilePictureUrl ? (
                            <img src={userData.profilePictureUrl} alt="" className="h-10 w-10 rounded-full object-cover shadow-sm"/>
                        ) : (
                            <FaUserCircle className="h-10 w-10 text-gray-300"/>
                        )}
                    </div>
                    <div className="flex-grow">
                        <textarea
                            value={newPost}
                            onChange={(e) => setNewPost(e.target.value)}
                            placeholder="What's on your mind?"
                            className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                            rows="3"
                            required
                        ></textarea>
                    </div>
                </div>
                <div className="flex justify-end mt-3">
                    <button 
                        type="submit" 
                        disabled={submitting || !newPost.trim()}
                        className="flex items-center gap-2 bg-primary text-white px-5 py-2 rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
                    >
                        <FaPaperPlane size={14}/> {submitting ? 'Posting...' : 'Post'}
                    </button>
                </div>
            </form>
        </div>

        {/* The Feed */}
        <div className="space-y-6">
            {loading ? (
                <div className="text-center py-10 text-gray-500 animate-pulse">Loading posts...</div>
            ) : posts.length > 0 ? (
                posts.map((post) => (
                    <PostItem 
                        key={post.id} 
                        post={post} 
                        familyId={familyData?.id} 
                        userData={userData} 
                        isHost={isHost} 
                        onDeletePost={handleDeletePost} 
                    />
                ))
            ) : (
                <div className="bg-white rounded-lg shadow-sm p-12 text-center border border-gray-100">
                    <FaComments className="text-5xl text-gray-200 mx-auto mb-4"/>
                    <h3 className="text-xl font-medium text-gray-700">No posts yet!</h3>
                    <p className="text-gray-500 mt-2">Be the first to start a conversation with the family.</p>
                </div>
            )}
        </div>
      </div>
    </Layout>
  );
};

export default FamilyFeedPage;